import type {CanvasEncoder, EncodeRequest} from "./offscreenImageCodec";

/**
 * The two ways this app can turn a canvas into WebP bytes (ui#601).
 *
 * WebP is the only output format the pipeline produces, and choosing it once is what collapses what
 * would otherwise be a format matrix: it carries an alpha channel, so a transparent logo survives, and
 * it beats JPEG at the same visual quality, so an opaque photo gets smaller. There is no branch on what
 * the source was.
 */

/** What both encoders produce. Checked against what the platform actually returned, never assumed. */
export const WEBP_TYPE = "image/webp";
const JPEG_TYPE = "image/jpeg";

/**
 * libwebp compiled to WebAssembly, loaded ON DEMAND.
 *
 * The dynamic import is the point: `@jsquash/webp` plus its `.wasm` is a few hundred KB that most
 * sessions never need, so it must not sit in the app's initial bundle. Vite code-splits this into its
 * own chunk and emits the `.wasm` as a same-origin asset, fetched the first time a sender picks a
 * picture that actually needs shrinking.
 *
 * The wasm is INSTANTIATED here rather than on first use. jSquash would otherwise initialise lazily
 * inside `encode`, which would surface an instantiation failure halfway through a quality search — too
 * late to fall back to the canvas encoder. Failing here instead keeps the fallback decision in one
 * place.
 *
 * @throws whatever the import or instantiation threw, for the caller to fall back on.
 */
export async function loadJsquashWebpEncoder(): Promise<CanvasEncoder> {
	const {default: encode, init} = await import("@jsquash/webp/encode");
	await init();
	return async ({canvas, quality}: EncodeRequest) => {
		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("The compression canvas lost its 2d context");
		}
		const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
		// libwebp takes an integer; the search bisects into fractions that would otherwise be truncated
		// inconsistently, making two nearby probes encode identically and stall the search.
		const encoded = await encode(pixels, {quality: Math.round(quality)});
		return new Blob([encoded], {type: WEBP_TYPE});
	};
}

/**
 * The canvas encoder — the fallback for when the wasm module cannot be fetched or instantiated (an
 * offline first pick, a corporate proxy that strips `application/wasm`, a browser with WebAssembly
 * switched off).
 *
 * `convertToBlob` does NOT fail when it cannot produce the format asked for: it silently returns PNG
 * instead. So the type is verified rather than trusted, and a substitution is handled explicitly.
 *
 * The JPEG rung exists because a browser that cannot encode WebP can almost certainly encode JPEG — but
 * it is refused for a picture with transparency. Every surface using this pipeline includes a LOGO
 * picker, and JPEG has no alpha channel: flattening one would silently paint a black box behind a
 * sender's mark, which is worse than telling them the picture could not be processed.
 */
export const canvasWebpEncoder: CanvasEncoder = async request => {
	const webp = await convert(request, WEBP_TYPE);
	if (webp.type === WEBP_TYPE) {
		return webp;
	}
	if (request.hasAlpha()) {
		throw new Error("This browser cannot encode WebP, and JPEG would discard the transparency");
	}
	const jpeg = await convert(request, JPEG_TYPE);
	if (jpeg.type !== JPEG_TYPE) {
		throw new Error(`This browser encoded ${jpeg.type || "an unknown format"}, which is not supported`);
	}
	return jpeg;
};

/** `convertToBlob` takes quality as a 0–1 fraction, unlike libwebp's 0–100 integer. */
async function convert({canvas, quality}: EncodeRequest, type: string): Promise<Blob> {
	return canvas.convertToBlob({type, quality: quality / 100});
}
